import { IUnitResponse } from '@kcftech/prometheustypescriptapiclient';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { sdClient } from 'contexts/SDKClientContext';
import {
    getUnitsRequest,
    loadBurstData,
} from 'custom_hooks/data_hooks/burst_data/useBurstData';
import { useCurrentUserInfo } from 'custom_hooks/data_hooks/user/useCurrentUserInfo';
import { BurstDataType, SelectedSeriesPoint } from 'pages/workbench/types';
import { mapBurstDataToPoints } from 'pages/workbench/workbench_tabs/trend/frequency_domain/shared';
import { useMemo } from 'react';
import { Point } from 'shared/chart/core/types';
import { sortBy } from 'utils/array';
import { queryClient } from 'utils/query';

const queryKey = 'trend-waterfall-data';

export function invalidateWaterfallFrequencyData() {
    queryClient.invalidateQueries({
        queryKey: [queryKey],
    });
}

export type TrendWaterfallFrequencyData = Readonly<{
    seriesId: string;
    timestamp: Date;
    seriesUnit?: string;
    dataPoints: Point[];
    seriesMinY: number;
    seriesMaxY: number;
    isVibrationType: boolean;
}>;

export type UseTrendWaterfallFrequencyDataOptions = Readonly<{
    selectedPoint: SelectedSeriesPoint;
    waterfallFrequencyPoints: SelectedSeriesPoint[];
    dataType: BurstDataType;
    useDataPlatform?: boolean;
    units?: IUnitResponse[];
}>;

export function useTrendWaterfallFrequencyData({
    selectedPoint,
    waterfallFrequencyPoints,
    dataType,
    useDataPlatform = false,
    units,
}: UseTrendWaterfallFrequencyDataOptions) {
    const { data: currentUser } = useCurrentUserInfo();
    const unitsRequest = getUnitsRequest(currentUser);

    const points = useMemo(
        () =>
            sortBy([selectedPoint, ...waterfallFrequencyPoints], (point) => point.xValue),
        [selectedPoint, waterfallFrequencyPoints]
    );

    const pointsKey = useMemo(
        () => points.map((point) => `${point.seriesId}-${point.xValue}`).join(','),
        [points]
    );

    return useQuery({
        queryKey: [
            queryKey,
            selectedPoint.seriesId,
            pointsKey,
            dataType,
            useDataPlatform,
            unitsRequest,
        ],

        queryFn: async () => {
            if (useDataPlatform) {
                const results = await Promise.all(
                    points.map(async (point) => ({
                        point,
                        waveform: await loadBurstData(point, unitsRequest),
                    }))
                );

                return results.map(({ point, waveform }) => {
                    // If there's only one data series, use it. Otherwise, find the one that matches the requested data type.
                    // This handles the case where a burst may not be in acceleration or velocity (e.g. pressure bursts).
                    const burstData =
                        waveform.data.length === 1
                            ? waveform.data[0]
                            : waveform.data.find(
                                  (series) => series.dataType === dataType
                              );

                    if (!burstData) {
                        throw new Error(
                            `Failed to find waveform data of type ${dataType}`
                        );
                    }

                    const mappedUnits =
                        units?.find((unit) => unit.Code === burstData.units)
                            ?.Abbreviation ?? burstData.units;

                    const dataPoints = mapBurstDataToPoints(
                        burstData.fft,
                        waveform.sampleRate
                    );
                    const mappedYs = dataPoints.map(({ y }) => y);
                    const isVibrationType =
                        burstData.dataType === 'acceleration' ||
                        burstData.dataType === 'velocity';

                    return {
                        seriesId: point.seriesId,
                        timestamp: new Date(point.xValue),
                        seriesUnit: mappedUnits,
                        seriesMinY: Math.min(...mappedYs),
                        seriesMaxY: Math.max(...mappedYs),
                        dataPoints,
                        isVibrationType,
                    };
                });
            }

            const results = await Promise.all(
                points.map(async (point) => ({
                    point,
                    data: await sdClient.getBurstData({
                        indicatorId: point.seriesId,
                        includeBaseline: false,
                        returnTimeDomain: false,
                        returnVelocity: dataType === 'velocity',
                        dateTimestamp: new Date(point.xValue),
                    }),
                }))
            );

            return results.map(({ point, data }) => {
                const burstData = data.BurstData[0];
                const dataPoints = mapBurstDataToPoints(burstData.Data, burstData.Rate);
                const mappedYs = dataPoints.map(({ y }) => y);
                const isVibrationType = data.IsVibrationTypeIndicator;

                return {
                    seriesId: point.seriesId,
                    timestamp: new Date(point.xValue),
                    seriesUnit: burstData.Units,
                    seriesMinY: Math.min(...mappedYs),
                    seriesMaxY: Math.max(...mappedYs),
                    dataPoints,
                    isVibrationType,
                };
            });
        },

        placeholderData: keepPreviousData,
    });
}
